import { FC, useMemo } from 'react';
import { Box } from '@mui/material';
import { IProductEntity } from '@/features/common/pricing';

import { StartFreeSection } from '@/components';

import { PlansOverview } from './components/PlansOverview';
import { ComparePlansTable } from './components/ComparePlansTable';
import { ClientTestimonials } from './components/ClientTestimonials';
import { FeatureHighlights } from './components/FeatureHighlights';
import { FAQSection } from './components/FAQSection';
import { usePricingPageViewModel } from './usePricingPageViewModel';
import { PricingPageContext } from './contexts/PricingPageContext';

import styles from './styles.module.scss';
import { Breadcrumbs } from '@/components/Breadcrumbs';
import { useTranslation } from 'next-i18next';
import { PlanFeaturesMetadataProvider } from './contexts';

export const PricingPage: FC<{
  isTrialEnabled: boolean;
  products: IProductEntity[];
}> = ({ isTrialEnabled, products }) => {
  const { t } = useTranslation('common');

  const { productsMetadata, maxAnnualDiscount, billingCycle, onChangeBillingCycle } =
    usePricingPageViewModel({ products });

  const contextValue = useMemo(
    () => ({
      productsMetadata,
      maxAnnualDiscount,
      billingCycle,
      onChangeBillingCycle,
      isTrialEnabled,
    }),
    [
      productsMetadata,
      maxAnnualDiscount,
      billingCycle,
      onChangeBillingCycle,
      isTrialEnabled,
    ],
  );

  return (
    <PricingPageContext.Provider value={contextValue}>
      <PlanFeaturesMetadataProvider productsMetadata={productsMetadata}>
        <Box className={styles.wrapper}>
          <Breadcrumbs
            float
            items={[
              {
                label: t('header.nav.pricing.title', { ns: 'common' }),
                url: '/pricing',
              },
            ]}
          />
          <PlansOverview />
          <ComparePlansTable />
          <ClientTestimonials />
          <FeatureHighlights />
          <FAQSection />
          <StartFreeSection
            title={t('startFree.title')}
            subtitle={t('startFree.subtitle')}
          />
        </Box>
      </PlanFeaturesMetadataProvider>
    </PricingPageContext.Provider>
  );
};
